// Thin API client. Every call attaches a bearer token acquired via MSAL and
// surfaces structured error bodies matching the server's DomainError shape
// ({error: {code, message, details?, requestId?}}).

import { env } from './env.js';

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
    requestId?: string;
  };
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly requestId: string | undefined;
  public readonly details: unknown;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error.code;
    this.requestId = body.error.requestId;
    this.details = body.error.details;
  }
}

export interface ApiCallOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  token: string;
  signal?: AbortSignal;
}

export async function apiCall<T>(path: string, opts: ApiCallOptions): Promise<T> {
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers: {
      authorization: `Bearer ${opts.token}`,
      ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    credentials: 'include',
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    ...(opts.signal !== undefined ? { signal: opts.signal } : {}),
  };
  const res = await fetch(`${env.VITE_API_BASE_URL}${path}`, init);

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const parsed = text ? (JSON.parse(text) as unknown) : undefined;

  if (!res.ok) {
    const body = (parsed ?? {
      error: { code: 'unknown', message: `HTTP ${res.status}` },
    }) as ApiErrorBody;
    throw new ApiError(res.status, body);
  }

  return parsed as T;
}
